<template>
  <section ref="rootEl" class="evpn-routes-section">
    <header class="evpn-routes-header">
      <div>
        <h4 class="evpn-routes-title">EVPN Routes</h4>
        <p class="evpn-routes-meta">
          Target: {{ target || "N/A" }}
          <span v-if="peerAddress"> | Peer: {{ peerAddress }}</span>
          <span v-else> | Peer: All</span>
        </p>
      </div>
      <button
        type="button"
        class="auto-refresh-toggle"
        :class="autoRefreshEnabled ? 'is-on' : 'is-off'"
        :disabled="loading"
        @click="toggleAutoRefresh"
      >
        <span class="auto-refresh-track">
          <span class="auto-refresh-knob"></span>
        </span>
        <span class="auto-refresh-label">Auto Refresh: {{ autoRefreshEnabled ? "ON" : "OFF" }}</span>
      </button>
    </header>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <div class="vm-vue-filter vm-list-filter-panel">
      <div class="vm-list-filter-panel-header">
        <div class="vm-list-filter-panel-title">EVPN Route Filters</div>
      </div>
      <div class="vm-vue-filter-grid vm-list-vue-filter-grid">
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">Peer</span>
          <div
            ref="peerPickerRef"
            class="vm-search-select vm-list-vue-filter-picker"
            :data-vue-open="peerPickerOpen ? 'true' : 'false'"
          >
            <button
              type="button"
              class="form-control vm-search-select-trigger"
              :aria-expanded="peerPickerOpen ? 'true' : 'false'"
              @click.stop="togglePeerPicker"
            >
              <span class="vm-search-select-trigger-label">{{ peerTriggerLabel }}</span>
              <span>{{ peerPickerOpen ? "▴" : "▾" }}</span>
            </button>

            <div v-if="peerPickerOpen" class="vm-search-select-panel" @click.stop>
              <input
                v-model="filters.peer"
                type="text"
                class="form-control vm-search-select-input"
                placeholder="Filter peer address"
              >
              <div class="vm-search-select-options">
                <button
                  type="button"
                  class="vm-search-select-option"
                  :class="{ 'is-selected': filters.peer === '' }"
                  @click="selectPeer('')"
                >
                  All peers
                </button>
                <button
                  v-for="peer in filteredPeerOptions"
                  :key="`peer-${peer}`"
                  type="button"
                  class="vm-search-select-option"
                  :class="{ 'is-selected': filters.peer === peer }"
                  @click="selectPeer(peer)"
                >
                  {{ peer }}
                </button>
                <div v-if="!filteredPeerOptions.length" class="vm-list-vue-filter-empty">
                  No matching peers
                </div>
              </div>
            </div>
          </div>
        </label>
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">Type</span>
          <div
            ref="typePickerRef"
            class="vm-search-select vm-list-vue-filter-picker"
            :data-vue-open="typePickerOpen ? 'true' : 'false'"
          >
            <button
              type="button"
              class="form-control vm-search-select-trigger"
              :aria-expanded="typePickerOpen ? 'true' : 'false'"
              @click.stop="toggleTypePicker"
            >
              <span class="vm-search-select-trigger-label">{{ typeTriggerLabel }}</span>
              <span>{{ typePickerOpen ? "▴" : "▾" }}</span>
            </button>

            <div v-if="typePickerOpen" class="vm-search-select-panel" @click.stop>
              <div class="vm-search-select-options">
                <button
                  type="button"
                  class="vm-search-select-option"
                  :class="{ 'is-selected': filters.type === '' }"
                  @click="selectType('')"
                >
                  All types
                </button>
                <button
                  v-for="type in typeOptions"
                  :key="`type-${type}`"
                  type="button"
                  class="vm-search-select-option"
                  :class="{ 'is-selected': filters.type === type }"
                  @click="selectType(type)"
                >
                  {{ type }}
                </button>
              </div>
            </div>
          </div>
        </label>
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">RD</span>
          <input v-model="filters.rd" type="text" class="form-control" placeholder="Filter RD">
        </label>
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">VNI</span>
          <input v-model="filters.vni" type="text" class="form-control" placeholder="Filter VNI">
        </label>
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">Mac/IP</span>
          <input v-model="filters.route" type="text" class="form-control" placeholder="Filter route">
        </label>
        <label class="vm-vue-filter-field vm-list-vue-filter-field">
          <span class="vm-vue-filter-label">RT</span>
          <input v-model="filters.rt" type="text" class="form-control" placeholder="Filter RT">
        </label>
        <div class="vm-vue-filter-actions">
          <button type="button" class="btn btn-default vm-list-vue-filter-reset" @click="resetFilters">Reset Filters</button>
        </div>
      </div>
    </div>

    <div class="table-responsive evpn-routes-scroll">
      <table class="table table-striped table-bordered table-condensed evpn-routes-table">
        <thead>
          <tr>
            <th>Peer Address</th>
            <th>EVPN Type</th>
            <th>RD</th>
            <th>VNI</th>
            <th>Etag</th>
            <th>Route (MAC/IP)</th>
            <th>NextHop</th>
            <th>Extended Community</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredRows.length && !loading">
            <td colspan="9" class="text-center">No EVPN routes found.</td>
          </tr>
          <tr v-for="(row, idx) in filteredRows" :key="`${row.peer_address}-${row.route}-${idx}`">
            <td>{{ row.peer_address }}</td>
            <td>{{ row.evpn_type }}</td>
            <td>{{ row.rd }}</td>
            <td class="text-right">{{ row.vni }}</td>
            <td class="text-right">{{ formatNumber(row.etag) }}</td>
            <td class="evpn-route-cell">{{ row.route }}</td>
            <td>{{ row.nexthop }}</td>
            <td class="evpn-extended-community">{{ row.extended_community }}</td>
            <td>{{ row.age }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  dataUrl: {
    type: String,
    required: true
  },
  peerAddress: {
    type: String,
    default: ""
  }
})

const rows = ref([])
const target = ref("")
const loading = ref(false)
const errorMessage = ref("")
const autoRefreshEnabled = ref(false)
const rootEl = ref(null)
const hasStartedInitialLoad = ref(false)
const peerPickerOpen = ref(false)
const typePickerOpen = ref(false)
const peerPickerRef = ref(null)
const typePickerRef = ref(null)
let refreshTimer = null
let visibilityObserver = null
const filters = ref({
  peer: "",
  type: "",
  rd: "",
  vni: "",
  route: "",
  rt: ""
})

const filteredRows = computed(() => {
  const peerKeyword = normalize(filters.value.peer)
  const typeKeyword = normalize(filters.value.type)
  const rdKeyword = normalize(filters.value.rd)
  const vniKeyword = normalize(filters.value.vni)
  const routeKeyword = normalize(filters.value.route)
  const rtKeyword = normalize(filters.value.rt)

  return rows.value.filter((row) => {
    if (peerKeyword && !normalize(row.peer_address).includes(peerKeyword)) return false
    if (typeKeyword && !normalize(row.evpn_type).includes(typeKeyword)) return false
    if (rdKeyword && !normalize(row.rd).includes(rdKeyword)) return false
    if (vniKeyword && !normalize(row.vni).includes(vniKeyword)) return false
    if (routeKeyword && !normalize(row.route).includes(routeKeyword)) return false

    if (rtKeyword) {
      const rtLines = String(row.extended_community || "")
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.toUpperCase().startsWith("RT "))
        .join(" ")
      if (!normalize(rtLines).includes(rtKeyword)) return false
    }

    return true
  })
})

const peerOptions = computed(() =>
  Array.from(
    new Set(
      rows.value
        .map((row) => String(row.peer_address || "").trim())
        .filter((peer) => peer !== "" && peer !== "-")
    )
  ).sort()
)

const filteredPeerOptions = computed(() => {
  const keyword = normalize(filters.value.peer)
  if (!keyword) return peerOptions.value
  return peerOptions.value.filter((peer) => normalize(peer).includes(keyword))
})

const typeOptions = computed(() =>
  Array.from(
    new Set(
      rows.value
        .map((row) => String(row.evpn_type || "").trim())
        .filter((type) => type !== "" && type !== "-")
    )
  ).sort()
)

const peerTriggerLabel = computed(() => {
  if (!filters.value.peer) return "Select peer"
  return filters.value.peer
})

const typeTriggerLabel = computed(() => {
  if (!filters.value.type) return "Select EVPN type"
  return filters.value.type
})

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

function normalize(value) {
  return String(value || "").trim().toLowerCase()
}

function resetFilters() {
  filters.value = {
    peer: "",
    type: "",
    rd: "",
    vni: "",
    route: "",
    rt: ""
  }
}

function togglePeerPicker() {
  peerPickerOpen.value = !peerPickerOpen.value
  if (peerPickerOpen.value) typePickerOpen.value = false
}

function toggleTypePicker() {
  typePickerOpen.value = !typePickerOpen.value
  if (typePickerOpen.value) peerPickerOpen.value = false
}

function selectPeer(value) {
  filters.value.peer = value
  peerPickerOpen.value = false
}

function selectType(value) {
  filters.value.type = value
  typePickerOpen.value = false
}

function closePickersOnOutsideClick(event) {
  if (!event.target.closest(".vm-search-select-panel") && !event.target.closest(".vm-search-select-trigger")) {
    peerPickerOpen.value = false
    typePickerOpen.value = false
  }
}

async function loadRows() {
  loading.value = true
  errorMessage.value = ""
  try {
    const response = await fetch(props.dataUrl, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
    const payload = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(payload?.message || `Request failed with status ${response.status}`)
    }

    target.value = payload?.target || ""
    rows.value = Array.isArray(payload?.rows) ? payload.rows : []
  } catch (error) {
    errorMessage.value = error?.message || "Failed to load EVPN routes."
  } finally {
    loading.value = false
  }
}

function stopAutoRefresh() {
  if (refreshTimer) {
    window.clearInterval(refreshTimer)
    refreshTimer = null
  }
}

function startAutoRefresh() {
  stopAutoRefresh()
  refreshTimer = window.setInterval(() => {
    if (!autoRefreshEnabled.value || document.visibilityState !== "visible" || loading.value) return
    loadRows()
  }, 5000)
}

function toggleAutoRefresh() {
  autoRefreshEnabled.value = !autoRefreshEnabled.value
  if (autoRefreshEnabled.value) {
    ensureInitialLoad()
    startAutoRefresh()
    if (!loading.value) loadRows()
    return
  }
  stopAutoRefresh()
}

function ensureInitialLoad() {
  if (hasStartedInitialLoad.value) return
  hasStartedInitialLoad.value = true
  loadRows()
}

onMounted(() => {
  if (typeof window !== "undefined" && "IntersectionObserver" in window && rootEl.value) {
    visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      ensureInitialLoad()
      visibilityObserver?.disconnect()
      visibilityObserver = null
    }, { rootMargin: "200px 0px" })
    visibilityObserver.observe(rootEl.value)
  } else {
    ensureInitialLoad()
  }
  document.addEventListener("click", closePickersOnOutsideClick)
  if (autoRefreshEnabled.value) {
    startAutoRefresh()
  }
})

onBeforeUnmount(() => {
  visibilityObserver?.disconnect()
  visibilityObserver = null
  document.removeEventListener("click", closePickersOnOutsideClick)
  stopAutoRefresh()
})
</script>

<style scoped>
.evpn-routes-section {
  position: relative;
}

.evpn-routes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.evpn-routes-title {
  margin: 0 0 4px;
}

.evpn-routes-meta {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.evpn-routes-table {
  margin-bottom: 0;
}

.evpn-routes-scroll {
  max-height: 65vh;
  overflow-y: auto;
}

.vm-vue-filter {
  margin-bottom: 14px;
}

.vm-list-filter-panel {
  padding: 18px 20px;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(241, 245, 249, 0.96));
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.vm-list-filter-panel-header {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vm-list-filter-panel-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
}

.vm-vue-filter-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
}

.vm-vue-filter-field {
  min-width: 220px;
  flex: 1 1 240px;
  margin: 0;
}

.vm-list-vue-filter-field {
  flex: 1 1 280px;
}

.vm-vue-filter-label {
  display: block;
  margin-bottom: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.vm-vue-filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vm-list-vue-filter-reset {
  min-width: 120px;
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.vm-list-vue-filter-reset:hover,
.vm-list-vue-filter-reset:focus {
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 100%);
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.16);
}

.vm-list-vue-filter-empty {
  padding: 10px 12px;
  color: #64748b;
  font-size: 12px;
}

.evpn-route-cell,
.evpn-extended-community {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 360px;
}

.auto-refresh-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 700;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.auto-refresh-toggle.is-on {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.auto-refresh-toggle.is-off {
  color: #7f1d1d;
  background: #fee2e2;
  border-color: #fca5a5;
}

.auto-refresh-track {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.2);
}

.auto-refresh-toggle.is-on .auto-refresh-track {
  background: #22c55e;
}

.auto-refresh-toggle.is-off .auto-refresh-track {
  background: #ef4444;
}

.auto-refresh-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  transition: transform 0.15s ease;
}

.auto-refresh-toggle.is-on .auto-refresh-knob {
  transform: translateX(16px);
}

.auto-refresh-label {
  white-space: nowrap;
}

@media (max-width: 767px) {
  .vm-vue-filter-grid {
    align-items: stretch;
  }

  .vm-vue-filter-field,
  .vm-vue-filter-actions {
    width: 100%;
    max-width: none;
  }
}
</style>
